import { Container, Graphics, Sprite, TilingSprite, type Texture } from 'pixi.js';
import { bakeFrames } from '../art/bake';
import { LANE_COATS, coatPaletteFor, exhaustedVariant } from '../art/palettes';
import type { PaletteInstance } from '../art/types';
import { GALLOP_FRAMES_PER_M, HORSE_GALLOP } from '../art/sprites/horse';
import { JOCKEY_RIDE, silksFor } from '../art/sprites/jockey';
import {
  CLOUD_BIG,
  CLOUD_SMALL,
  DIRT_TILE,
  FENCE,
  FLAG_POST,
  GRANDSTAND,
  GRASS_TILE,
  STARTING_GATE,
  TREE_BIG,
} from '../art/sprites/scenery';
import { DustField, StrideEmitter } from './effects';
import type { RaceSetup, RaceState } from '../sim/race/types';
import { VIRTUAL_H, VIRTUAL_W } from './StageScaler';

const PX_PER_M = 20;
const START_PAD = 200;
const FINISH_PAD = 420;
const CAMERA_LEAD = 170;

const SKY_H = 46;
const INFIELD_TOP = SKY_H;
const RAIL_TOP = 96;
const DIRT_TOP = 110;
const DIRT_BOTTOM = 256;

/** Hoof-line rows, furthest lane first; the player (lane 0) runs closest. */
function hoofY(lane: number, laneCount: number): number {
  return 148 + (laneCount - 1 - lane) * 20;
}

/** Gallop stride: ~4.5 m per 8-frame cycle, driven purely by sim distance
 * — visual state stays a function of sim state, and slow-mo replays are
 * automatic later. */


function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Race day, dressed: grandstands, rail, dirt, and the pack. */
export class RaceRenderer {
  private readonly container = new Container();
  private readonly world = new Container();
  private readonly clouds: Sprite[] = [];
  private readonly horses: Sprite[] = [];
  private readonly jockeys: Sprite[] = [];
  private readonly jockeyFrames: Texture[][] = [];
  private readonly emitters: StrideEmitter[] = [];
  private dust!: DustField;
  private lastDrawMs = 0;
  private readonly laneFrames: Texture[][] = [];
  private readonly laneBlownFrames: Texture[][] = [];
  private readonly playerMarker: Graphics;
  private readonly setup: RaceSetup;
  private readonly worldW: number;

  constructor(parent: Container, setup: RaceSetup, playerPalette?: PaletteInstance) {
    this.setup = setup;
    this.worldW = START_PAD + setup.distanceM * PX_PER_M + FINISH_PAD;
    parent.addChild(this.container);
    const laneCount = setup.opponents.length + 1;

    // Static sky.
    const sky = new Graphics()
      .rect(0, 0, VIRTUAL_W, 22)
      .fill(0x8fb0d0)
      .rect(0, 22, VIRTUAL_W, SKY_H - 22)
      .fill(0x87a8c8);
    this.container.addChild(sky);
    for (const [piece, x, y] of [
      [CLOUD_BIG, 70, 12],
      [CLOUD_SMALL, 240, 6],
      [CLOUD_SMALL, 390, 20],
    ] as const) {
      const cloud = new Sprite(bakeFrames(piece.def, piece.palette)[0]!);
      cloud.position.set(x, y);
      this.container.addChild(cloud);
      this.clouds.push(cloud);
    }

    this.container.addChild(this.world);

    // Infield grass behind the stands, then the scrolling dressing.
    const infield = new Graphics()
      .rect(0, INFIELD_TOP, this.worldW, RAIL_TOP - INFIELD_TOP)
      .fill(0x5d7a44)
      .rect(0, DIRT_BOTTOM, this.worldW, VIRTUAL_H - DIRT_BOTTOM)
      .fill(0x42522f);
    this.world.addChild(infield);

    const grassTex = bakeFrames(GRASS_TILE.def, GRASS_TILE.palette)[0]!;
    const verge = new TilingSprite({ texture: grassTex, width: this.worldW, height: 10 });
    verge.position.set(0, RAIL_TOP - 10 + 10); // thin bright strip under the fence
    verge.y = RAIL_TOP;
    verge.height = DIRT_TOP - RAIL_TOP;
    this.world.addChild(verge);

    const standTex = bakeFrames(GRANDSTAND.def, GRANDSTAND.palette)[0]!;
    const treeTex = bakeFrames(TREE_BIG.def, TREE_BIG.palette)[0]!;
    for (let x = 160; x < this.worldW - 100; x += 560) {
      const stand = new Sprite(standTex);
      stand.position.set(x, RAIL_TOP - GRANDSTAND.def.h);
      this.world.addChild(stand);
      const tree = new Sprite(treeTex);
      tree.position.set(x + 340, RAIL_TOP - TREE_BIG.def.h + 2);
      this.world.addChild(tree);
    }

    const dirtTex = bakeFrames(DIRT_TILE.def, DIRT_TILE.palette)[0]!;
    const dirt = new TilingSprite({
      texture: dirtTex,
      width: this.worldW,
      height: DIRT_BOTTOM - DIRT_TOP,
    });
    dirt.position.set(0, DIRT_TOP);
    this.world.addChild(dirt);

    const fenceTex = bakeFrames(FENCE.def, FENCE.palette)[0]!;
    const rail = new TilingSprite({ texture: fenceTex, width: this.worldW, height: FENCE.def.h });
    rail.position.set(0, DIRT_TOP - FENCE.def.h);
    this.world.addChild(rail);

    // Start gate + start line.
    const gate = new Sprite(bakeFrames(STARTING_GATE.def, STARTING_GATE.palette)[0]!);
    gate.position.set(START_PAD - STARTING_GATE.def.w - 8, DIRT_TOP - 24);
    this.world.addChild(gate);
    const markers = new Graphics();
    markers.rect(START_PAD - 2, DIRT_TOP, 3, DIRT_BOTTOM - DIRT_TOP).fill({ color: 0xe8e2d2, alpha: 0.7 });

    // Distance posts every 50 m, flags every 100 m, checkered finish.
    const flagTex = bakeFrames(FLAG_POST.def, FLAG_POST.palette)[0]!;
    for (let m = 50; m < setup.distanceM; m += 50) {
      const x = START_PAD + m * PX_PER_M;
      if (m % 100 === 0) {
        const flag = new Sprite(flagTex);
        flag.position.set(x - 3, DIRT_TOP - FLAG_POST.def.h - 8);
        this.world.addChild(flag);
      } else {
        markers.rect(x, DIRT_TOP - 18, 2, 18).fill({ color: 0xd8d0c0, alpha: 0.8 });
      }
    }
    const finishX = START_PAD + setup.distanceM * PX_PER_M;
    const square = 6;
    for (let sy = 0; sy * square < DIRT_BOTTOM - DIRT_TOP; sy += 1) {
      for (let sx = 0; sx < 2; sx += 1) {
        markers
          .rect(finishX + sx * square, DIRT_TOP + sy * square, square, Math.min(square, DIRT_BOTTOM - DIRT_TOP - sy * square))
          .fill((sy + sx) % 2 === 0 ? 0x1a1a1a : 0xe8e8e8);
      }
    }
    this.world.addChild(markers);
    const finishFlag = new Sprite(flagTex);
    finishFlag.position.set(finishX - 2, DIRT_TOP - FLAG_POST.def.h - 8);
    this.world.addChild(finishFlag);

    // Dust hangs behind the field.
    this.dust = new DustField(this.world, 60);

    // The field, far lanes first so the player draws in front.
    for (let lane = laneCount - 1; lane >= 0; lane -= 1) {
      this.emitters[lane] = new StrideEmitter();
      const rivalCoatId = lane > 0 ? setup.opponents[lane - 1]?.coatId : undefined;
      const coat =
        lane === 0 && playerPalette
          ? playerPalette
          : rivalCoatId
            ? coatPaletteFor(rivalCoatId)
            : (LANE_COATS[lane] ?? LANE_COATS[0]!);
      this.laneFrames[lane] = bakeFrames(HORSE_GALLOP, coat);
      this.laneBlownFrames[lane] = bakeFrames(HORSE_GALLOP, exhaustedVariant(coat));
      const sprite = new Sprite(this.laneFrames[lane]![0]!);
      this.world.addChild(sprite);
      this.horses[lane] = sprite;
      // Silks ride the same canvas and anchor as the horse, so the
      // jockey is a straight overlay at identical coordinates.
      this.jockeyFrames[lane] = bakeFrames(JOCKEY_RIDE, silksFor(lane));
      const jockey = new Sprite(this.jockeyFrames[lane]![0]!);
      this.world.addChild(jockey);
      this.jockeys[lane] = jockey;
    }

    this.playerMarker = new Graphics()
      .poly([0, 0, 8, 0, 4, 5])
      .fill(0xd9a441);
    this.world.addChild(this.playerMarker);
  }

  draw(state: RaceState, alpha: number): void {
    const laneCount = state.lanes.length;
    const player = state.lanes[0]!;
    const playerX = START_PAD + lerp(player.prevX, player.x, alpha) * PX_PER_M;
    const camX = Math.max(0, Math.min(playerX - CAMERA_LEAD, this.worldW - VIRTUAL_W));
    this.world.position.x = -Math.round(camX);

    this.clouds.forEach((cloud, index) => {
      cloud.x = ((70 + index * 160 - camX * 0.12 - index * 8) % (VIRTUAL_W + 60)) + -40;
    });

    // Effects run on wall-clock time; the sim never sees them.
    const nowMs = performance.now();
    const dtS = this.lastDrawMs === 0 ? 0 : Math.min(0.05, (nowMs - this.lastDrawMs) / 1000);
    this.lastDrawMs = nowMs;
    this.dust.update(dtS);

    state.lanes.forEach((lane, index) => {
      const sprite = this.horses[index];
      if (!sprite) return;
      const xM = lerp(lane.prevX, lane.x, alpha);
      const frames = lane.exhausted ? this.laneBlownFrames[index]! : this.laneFrames[index]!;
      const frame =
        lane.v > 0.5 ? Math.floor(xM * GALLOP_FRAMES_PER_M) % frames.length : 0;
      const groundY = hoofY(index, laneCount);
      const centerX = START_PAD + xM * PX_PER_M;
      if (lane.v > 2 && this.emitters[index]?.step(xM)) {
        this.dust.emit(centerX - 12, groundY, Math.min(1.4, lane.v / 11));
      }
      sprite.texture = frames[frame]!;
      const spriteX = Math.round(centerX - HORSE_GALLOP.anchor[0]);
      const spriteY = Math.round(groundY - HORSE_GALLOP.h);
      sprite.position.set(spriteX, spriteY);
      const jockey = this.jockeys[index];
      if (jockey) {
        jockey.texture = this.jockeyFrames[index]![frame % this.jockeyFrames[index]!.length]!;
        jockey.position.set(spriteX, spriteY);
      }
    });

    const marker = this.playerMarker;
    const playerSprite = this.horses[0]!;
    marker.position.set(
      playerSprite.position.x + HORSE_GALLOP.anchor[0] - 4,
      playerSprite.position.y - 9 + Math.round(Math.sin(state.tick * 0.2) * 1.5),
    );
  }

  /** Photo replay: drive positions straight from recorded history,
   * camera glued to the leader at the wire. */
  drawRaw(xsM: number[]): void {
    const laneCount = xsM.length;
    this.dust.update(1 / 240); // slow-mo: dust drifts with the replay
    const leaderX = Math.max(...xsM);
    const camX = Math.max(
      0,
      Math.min(START_PAD + leaderX * PX_PER_M - VIRTUAL_W + 120, this.worldW - VIRTUAL_W),
    );
    this.world.position.x = -Math.round(camX);
    xsM.forEach((xM, index) => {
      const sprite = this.horses[index];
      if (!sprite) return;
      const frames = this.laneFrames[index]!;
      const frame = Math.floor(xM * GALLOP_FRAMES_PER_M) % frames.length;
      sprite.texture = frames[frame]!;
      const y = hoofY(index, laneCount);
      const jockey = this.jockeys[index];
      if (jockey) {
        jockey.texture = this.jockeyFrames[index]![frame % this.jockeyFrames[index]!.length]!;
        jockey.position.set(
          Math.round(START_PAD + xM * PX_PER_M - HORSE_GALLOP.anchor[0]),
          Math.round(y - HORSE_GALLOP.h),
        );
      }
      sprite.position.set(
        Math.round(START_PAD + xM * PX_PER_M - HORSE_GALLOP.anchor[0]),
        Math.round(y - HORSE_GALLOP.h),
      );
    });
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
